import { t } from '@lingui/macro'
import { usePatientTasks } from '@om1/aspen-api'
import { PatientTaskDTO } from '@om1/aspen-api/codegen/client/models/PatientTaskDTO'
import { Check, Clock, X } from 'lucide-react'
import React from 'react'
import { Column, GenericDataPage } from './GenericDataPage'

// Status badge component for better visual representation
const StatusBadge = ({ status }: { status: string }) => {
    let bgColor = 'bg-gray-100'
    let textColor = 'text-gray-700'
    let icon: React.ReactNode | null = null
    let description = t`Unknown status`

    // Map status to appropriate colors, icons, and descriptions
    if (status === 'FAILURE') {
        bgColor = 'bg-red-100'
        textColor = 'text-red-800'
        icon = <X size={16} className='mr-1' />
        description = t`Task failed`
    } else if (status === 'SUCCESS') {
        bgColor = 'bg-green-100'
        textColor = 'text-green-800'
        icon = <Check size={16} className='mr-1' />
        description = t`Task completed successfully`
    } else if (status === 'PENDING') {
        bgColor = 'bg-blue-100'
        textColor = 'text-blue-800'
        icon = <Clock size={16} className='mr-1' />
        description = t`Task is pending`
    } else if (status === 'STARTED') {
        bgColor = 'bg-yellow-100'
        textColor = 'text-yellow-800'
        icon = <Clock size={16} className='mr-1' />
        description = t`Task has started`
    } else {
        icon = <Clock size={16} className='mr-1' />
    }

    const displayStatus = status.replace(/_/g, ' ').toLowerCase()

    return (
        <div
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${bgColor} ${textColor} text-sm font-medium shadow-sm`}
            title={description}
        >
            {icon}
            <span className='capitalize'>{displayStatus}</span>
        </div>
    )
}

const patientTaskColumns: Column<PatientTaskDTO>[] = [
    { key: 'taskId', type: 'text' },
    { key: 'patientId', type: 'text' },
    { key: 'task.name', type: 'text', disableSort: true, displayValue: (value) => value.replace('om1.modules.aspen.worker.tasks.', '') },
    { key: 'taskCreatedDttm', type: 'date' }
]

const TasksPage: React.FC = () => {
    return (
        <GenericDataPage<PatientTaskDTO, 'taskId'>
            useData={usePatientTasks}
            useDataArgs={[]}
            columns={patientTaskColumns as Column<PatientTaskDTO>[]}
            title={t`Patient Tasks`}
            detailPageRoute={(taskId: string) => `/tasks/${taskId}`}
            customColumns={[
                {
                    label: t`Status`,
                    value: (item: PatientTaskDTO) => (item.task?.status ? <StatusBadge status={item.task.status} /> : null)
                }
            ]}
        />
    )
}

export default TasksPage
